import { useQuery } from '@tanstack/react-query'

export interface ReportType {
  key: string
  value: string
}

const reportsUrl =
  import.meta.env.VITE_REPORT_SERVICE_URI ||
  import.meta.env.VITE_REPORT_API_URL ||
  'https://reports.api.coredatastore.com/api'

/** Report types from the Reporting API, e.g. { key: "lpclandmarks", value: "LpcLandmarks" }. */
export function useReportTypes(enabled: boolean) {
  return useQuery({
    queryKey: ['report-types'],
    queryFn: async () => {
      const response = await globalThis.fetch(
        `${reportsUrl}/Reference/reportTypes`,
      )
      if (!response.ok)
        throw new Error(`Reporting API request failed: ${response.status}`)
      return (await response.json()) as ReportType[]
    },
    enabled,
    staleTime: Infinity,
  })
}

/** "2026-09-25-03-37-28", the timestamp in downloaded report file names. */
export function fileTimestamp(date = new Date()) {
  const p = (n: number) => String(n).padStart(2, '0')
  return `${date.getFullYear()}-${p(date.getMonth() + 1)}-${p(date.getDate())}-${p(date.getHours())}-${p(date.getMinutes())}-${p(date.getSeconds())}`
}

/** Downloads an Excel report and saves it as report_<key>_<timestamp>.xlsx. */
export async function downloadReport(reportKey: string) {
  const response = await globalThis.fetch(
    `${reportsUrl}/ExcelReports?ReportType=${encodeURIComponent(reportKey)}`,
    {
      headers: { Accept: 'application/vnd.ms-excel' },
    },
  )
  if (!response.ok)
    throw new Error(`Report download failed: ${response.status}`)
  const url = URL.createObjectURL(await response.blob())
  const link = document.createElement('a')
  link.href = url
  link.download = `report_${reportKey}_${fileTimestamp()}.xlsx`
  document.body.appendChild(link)
  link.click()
  link.remove()
  URL.revokeObjectURL(url)
}
